import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useVelocity, useAnimationFrame } from "motion/react";
import { MoveRight } from "lucide-react";

// ----------------------------------------------------------------------------
// 1. IMMERSIVE HERO
// ----------------------------------------------------------------------------
export const CinematicHero = ({ title, subtitle, bgImage, ctaText, ctaLink, bgSize = "cover", children, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const opacityText = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  return (
    <div ref={ref} className={`relative h-screen w-full overflow-hidden flex items-center justify-center bg-black ${className}`}>
      <motion.div 
        style={{ scale: scaleBg, backgroundImage: `url(${bgImage})` }}
        className={`absolute inset-0 bg-${bgSize} bg-no-repeat bg-center z-0`}
      />
      {/* Cinematic Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black z-10" />
      <div className="absolute inset-0 bg-black/40 z-10" />

      <motion.div 
        style={{ y: yText, opacity: opacityText }}
        className="relative z-20 w-full h-full flex items-center justify-center p-6"
      >
        {children || (
          <div className="text-center max-w-7xl mx-auto flex flex-col items-center">
            <motion.h1 
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl md:text-9xl font-medium text-white tracking-tighter mb-6 leading-none"
            >
              {title}
            </motion.h1>
            
            {subtitle && (
              <motion.p 
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
                className="text-xl md:text-3xl text-gray-300 max-w-3xl font-regular tracking-wide"
              >
                {subtitle}
              </motion.p>
            )}

            {ctaLink && (
              <motion.a
                href={ctaLink}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="mt-12 group relative inline-flex items-center gap-3 px-8 py-4 bg-white text-black rounded-full font-medium text-lg overflow-hidden hover:bg-gray-200 transition-colors"
              >
                <span>{ctaText}</span>
                <MoveRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// 2. STACKING CARD SECTION (Sticky Reveal)
// ----------------------------------------------------------------------------
export const StackingSection = ({ children, className = "", index }) => {
    return (
        <div className="relative h-screen">
            <div className={`sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden bg-black border-t border-white/5 shadow-2xl ${className}`} style={{ zIndex: index }}>
                <div className="w-full h-full relative">
                    {children}
                </div>
            </div>
        </div>
    );
};

// ----------------------------------------------------------------------------
// 3. HORIZONTAL SCROLL GALLERY
// ----------------------------------------------------------------------------
export const HorizontalScrollCarousel = ({ items, title }) => {
    const targetRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: targetRef,
    });

    const x = useTransform(scrollYProgress, [0, 1], ["0%", "-75%"]);

    return (
        <section ref={targetRef} className="relative h-[300vh] bg-black z-[100]">
            <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden pl-16 bg-black z-[101]">
                 {title && (
                    <motion.h2 
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        className="text-5xl md:text-7xl font-medium text-white mb-12 tracking-tighter"
                    >
                        {title}
                    </motion.h2>
                 )}
                <motion.div style={{ x }} className="flex gap-10">
                    {items.map((item, i) => (
                        <div key={i} className="group relative h-[60vh] w-[40vw] flex-shrink-0 overflow-hidden rounded-[32px] bg-black border border-white/5">
                             {/* Ambient Blue Shade */}
                             <div className="absolute inset-0 bg-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                             
                             {item.image && (
                                <img 
                                    src={item.image} 
                                    alt={item.title} 
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-40 group-hover:opacity-60" 
                                />
                             )}
                             <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent p-8 flex flex-col justify-end">
                                <h3 className="text-4xl font-medium text-white mb-4 tracking-tight group-hover:text-blue-400 transition-colors uppercase">{item.title}</h3>
                                <p className="text-xl text-gray-400 leading-relaxed font-light group-hover:text-gray-200 transition-colors">{item.description}</p>
                             </div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

// ----------------------------------------------------------------------------
// 3.1 CINEMATIC MASK REVEAL
// ----------------------------------------------------------------------------
export const CinematicMaskReveal = ({ children, image, title }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });
    
    // Smooth clip path transition from a rounded rectangle to full screen
    const clipPath = useTransform(
        scrollYProgress, 
        [0, 0.4, 0.6, 1], 
        ["inset(20% round 60px)", "inset(0% round 0px)", "inset(0% round 0px)", "inset(20% round 60px)"]
    );

    const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.2, 1, 1.2]);

    return (
        <div ref={ref} className="h-[200vh] relative bg-black">
            <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden">
                <motion.div style={{ clipPath }} className="relative w-full h-full">
                    <motion.img 
                        src={image} 
                        style={{ scale }}
                        className="absolute inset-0 w-full h-full object-cover" 
                    />
                    <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-center px-6">
                        {title && (
                             <motion.h2 
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                className="text-6xl md:text-9xl font-medium text-white tracking-tighter mb-8"
                             >
                                 {title}
                             </motion.h2>
                        )}
                        {children}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

// ----------------------------------------------------------------------------
// 3.2 AMBIENT PARTICLES
// ----------------------------------------------------------------------------
export const AmbientParticles = () => {
    return (
        <div className="fixed inset-0 pointer-events-none z-0">
             {[...Array(20)].map((_, i) => (
                <motion.div
                    key={i}
                    initial={{ 
                        opacity: Math.random() * 0.5, 
                        x: Math.random() * 100 + "%", 
                        y: Math.random() * 100 + "%" 
                    }}
                    animate={{ 
                        y: ["-10%", "110%"],
                        opacity: [0, 1, 0]
                    }}
                    transition={{ 
                        duration: Math.random() * 10 + 10, 
                        repeat: Infinity, 
                        ease: "linear",
                        delay: Math.random() * 20
                    }}
                    className="absolute w-1 h-1 bg-blue-500 rounded-full blur-[1px]"
                />
             ))}
             <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,_rgba(0,102,255,0.05)_0%,_transparent_50%)]" />
        </div>
    );
};

// ----------------------------------------------------------------------------
// 4. SPOTLIGHT CARD (Mouse Follow Effect)
// ----------------------------------------------------------------------------
export const SpotlightCard = ({ children, className = "" }) => {
  const divRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);

  const handleMouseMove = (e) => {
    if (!divRef.current) return;
    const div = divRef.current;
    const rect = div.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseEnter = () => {
    setOpacity(1);
  };

  const handleMouseLeave = () => {
    setOpacity(0);
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative rounded-[32px] border border-white/5 bg-black/40 backdrop-blur-3xl overflow-hidden ${className}`}
    >
      <div
        className="pointer-events-none absolute -inset-px opacity-0 transition duration-300"
        style={{
          opacity,
          background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, rgba(255,255,255,0.1), transparent 40%)`,
        }}
      />
      <div className="relative h-full">{children}</div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// 5. TEXT REVEAL (Character by Character)
// ----------------------------------------------------------------------------
export const CinematicText = ({ children, className = "" }) => {
    return (
        <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className={`font-medium tracking-tight ${className}`}
        >
            {children}
        </motion.h2>
    );
};

// ----------------------------------------------------------------------------
// 6. VELOCITY SCROLL (Marquee)
// ----------------------------------------------------------------------------
export const VelocityScroll = ({ text, default_velocity = 5, className = "" }) => {
    const { scrollY } = useScroll();
    const scrollVelocity = useVelocity(scrollY);
    const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 });
    const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 5], { clamp: false });
  
    const x = useMotionValue(0);
    const directionFactor = useRef(1);
    
    useAnimationFrame((t, delta) => {
      let moveBy = directionFactor.current * default_velocity * (delta / 1000);
      if (velocityFactor.get() < 0) {
        directionFactor.current = -1;
      } else if (velocityFactor.get() > 0) {
        directionFactor.current = 1;
      }
      moveBy += directionFactor.current * moveBy * velocityFactor.get();
      x.set(x.get() + moveBy);
    });
  
    return (
      <div className={`overflow-hidden m-0 whitespace-nowrap flex flex-nowrap ${className}`}>
        <motion.div className="flex flex-nowrap text-8xl md:text-9xl font-medium uppercase tracking-tighter opacity-10" style={{ x }}>
          <span className="block mr-8">{text} </span>
          <span className="block mr-8">{text} </span>
          <span className="block mr-8">{text} </span>
          <span className="block mr-8">{text} </span>
        </motion.div>
      </div>
    );
};

// ----------------------------------------------------------------------------
// 7. MASONRY GRID (Non-Sticky)
// ----------------------------------------------------------------------------
// ----------------------------------------------------------------------------
// 7. MASONRY GRID (Staggered Animation)
// ----------------------------------------------------------------------------
export const MasonryGrid = ({ items }) => {
    return (
        <section className="py-32 px-6 bg-black z-30 relative">
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.map((item, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                        className={i === 1 || i === 4 ? 'lg:translate-y-12' : ''}
                    >
                        <SpotlightCard className="p-8 bg-black/60 border-white/5 h-full group hover:border-blue-500/20 transition-all duration-500">
                            {/* Blue Accent Glow */}
                            <div className="absolute inset-0 bg-blue-600/[0.02] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                            {item.icon && <div className="mb-6 relative z-10 text-blue-500">{item.icon}</div>}
                            <h3 className="text-2xl font-semibold text-white mb-3 tracking-tight relative z-10 group-hover:text-blue-400 transition-colors uppercase">{item.title}</h3>
                            <p className="text-gray-400 leading-relaxed font-light relative z-10 group-hover:text-gray-300 transition-colors">{item.description}</p>
                        </SpotlightCard>
                    </motion.div>
                ))}
            </div>
        </section>
    );
};

// ----------------------------------------------------------------------------
// 8. CINEMATIC CARD STACK (Scrollytelling)
// ----------------------------------------------------------------------------
export const CinematicCardStack = ({ items }) => {
  return (
    <div className="relative w-full bg-black py-32 px-4 md:px-8">
      <div className="max-w-5xl mx-auto space-y-32">
        {items.map((item, index) => (
          <CardStackItem key={index} item={item} index={index} total={items.length} />
        ))}
      </div>
    </div>
  );
};

const CardStackItem = ({ item, index, total }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0]);
  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <motion.div
      ref={ref}
      style={{ scale, opacity, y }}
      className="sticky top-24 md:top-32 flex flex-col items-center justify-center min-h-[60vh] md:min-h-[70vh] w-full p-6 md:p-16 bg-black/60 backdrop-blur-3xl border border-white/5 rounded-[40px] overflow-hidden shadow-2xl group"
    >
        {/* Background Glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-transparent pointer-events-none" />
        
        <div className="relative z-10 flex flex-col md:flex-row items-center gap-12 text-center md:text-left">
            <div className="flex-1 space-y-6">
                {item.icon && <div className="text-blue-500 mb-4">{item.icon}</div>}
                <h3 className="text-4xl md:text-6xl font-semibold text-white tracking-tighter">{item.title}</h3>
                <p className="text-xl md:text-2xl text-gray-400 font-light leading-relaxed max-w-lg">{item.description}</p>
            </div>
            {item.image && (
                <div className="flex-1 w-full h-[300px] md:h-[400px] rounded-[32px] overflow-hidden shadow-2xl border border-white/5">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                </div>
            )}
        </div>
    </motion.div>
  );
};

// ----------------------------------------------------------------------------
// 9. PARALLAX FEATURES (Alternating Layout)
// ----------------------------------------------------------------------------
export const ParallaxFeatures = ({ items }) => {
    return (
        <section className="py-32 bg-black px-6 md:px-12 relative overflow-hidden">
             {items.map((item, index) => (
                <ParallaxFeatureItem key={index} item={item} index={index} />
             ))}
        </section>
    );
};

const ParallaxFeatureItem = ({ item, index }) => {
    const isEven = index % 2 === 0;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    const x = useTransform(scrollYProgress, [0, 1], isEven ? [-100, 100] : [100, -100]);
    const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

    return (
        <div ref={ref} className={`flex flex-col md:flex-row items-center gap-12 md:gap-24 mb-32 ${isEven ? '' : 'md:flex-row-reverse'}`}>
            <motion.div style={{ opacity, x: useTransform(scrollYProgress, [0, 1], isEven ? [-50, 0] : [50, 0]) }} className="flex-1 space-y-6">
                {item.icon && (
                    <div className="w-16 h-16 rounded-[32px] bg-white/5 flex items-center justify-center text-blue-400 mb-6 border border-white/10">
                        {item.icon}
                    </div>
                )}
                <h3 className="text-4xl md:text-5xl font-semibold text-white tracking-tight">{item.title}</h3>
                <p className="text-xl text-gray-400 font-light leading-relaxed">{item.description}</p>
            </motion.div>
            
            <motion.div style={{ opacity, scale: useTransform(scrollYProgress, [0, 1], [0.8, 1]) }} className="flex-1 w-full h-[400px] bg-black rounded-[40px] overflow-hidden border border-white/5 relative group">
                {item.image ? (
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-80" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-900 to-black">
                         <div className="w-32 h-32 bg-blue-500/20 blur-[60px] rounded-full animate-pulse" />
                    </div>
                )}
                <div className="absolute inset-0 bg-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
            </motion.div>
        </div>
    );
};

// ----------------------------------------------------------------------------
// 10. IMMERSIVE SCROLL TRACK (Full Page Visuals)
// ----------------------------------------------------------------------------
export const ImmersiveScrollTrack = ({ items }) => {
    return (
        <div className="bg-black">
            {items.map((item, index) => (
                <ImmersiveFeatureStep key={index} item={item} index={index} />
            ))}
        </div>
    );
};

const ImmersiveFeatureStep = ({ item, index }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });

    const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1, 0.9]);
    const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
    const yText = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [100, 0, 0, -100]);

    const isTextOnly = item.type === "text-only";

    return (
        <div ref={ref} className="h-[200vh] relative">
            <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden">
                {!isTextOnly && (
                    <motion.div 
                        style={{ scale, opacity }}
                        className="absolute inset-0 z-0 flex items-center justify-center p-20"
                    >
                        {item.image ? (
                            <img 
                                src={item.image} 
                                alt="" 
                                className={`w-full h-full ${item.title === "S.H.A.D.E" ? "object-contain scale-[0.85] opacity-40 py-24" : "object-cover opacity-50"}`} 
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-zinc-900 to-black" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black" />
                    </motion.div>
                )}

                {/* Content Overlay */}
                <motion.div 
                    style={{ opacity, y: yText }}
                    className={`relative z-10 text-center ${item.title === "S.H.A.D.E" ? "w-full px-4" : "max-w-5xl px-6"}`}
                >
                    {item.icon && (
                        <div className={`${isTextOnly ? 'text-blue-500 mb-12 scale-150' : 'text-blue-500 mb-8'} flex justify-center animate-pulse`}>
                            {item.icon}
                        </div>
                    )}

                    {item.title === "S.H.A.D.E" ? (
                         <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center z-50 pointer-events-none">
                             {/* The Background Glow (Optimized for Mobile GPU) */}
                             <motion.div 
                                animate={{ opacity: [0.1, 0.3, 0.1] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                                className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[40vh] bg-blue-500/30 blur-[180px] z-0"
                             />
                             <motion.div 
                                animate={{ opacity: [0.05, 0.15, 0.05] }}
                                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                                className="md:hidden absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[40vh] bg-blue-500/20 blur-3xl z-0"
                             />
                             
                             <div className="relative z-10 flex items-start justify-center">
                                 {[
                                     { l: "S", w: "Secure" },
                                     { dot: true },
                                     { l: "H", w: "Hostile" },
                                     { dot: true },
                                     { l: "A", w: "Adaptive" },
                                     { dot: true },
                                     { l: "D", w: "Defense" },
                                     { dot: true },
                                     { l: "E", w: "Engine" }
                                 ].map((part, i) => (
                                     <React.Fragment key={i}>
                                         {part.dot ? (
                                             <motion.h2 
                                                initial={{ opacity: 0 }}
                                                whileInView={{ opacity: 1 }}
                                                transition={{ duration: 1.5, delay: 0.5 }}
                                                className="text-4xl md:text-[8.5vw] font-medium text-white leading-none px-1"
                                             >
                                                .
                                             </motion.h2>
                                         ) : (
                                             <div className="flex flex-col items-center">
                                                 <motion.h2 
                                                    initial={{ opacity: 0, y: 20 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 1, delay: i * 0.1 }}
                                                    className="text-4xl md:text-[8.5vw] font-medium text-white uppercase text-center leading-none relative"
                                                    style={{
                                                        textShadow: "0 0 40px rgba(255,255,255,0.4)"
                                                    }}
                                                 >
                                                    {part.l}
                                                    {/* Local shimmer for each letter to keep it looking "alive" */}
                                                    <motion.div 
                                                        animate={{ opacity: [0.3, 0.6, 0.3] }}
                                                        transition={{ duration: 3, repeat: Infinity }}
                                                        className="absolute inset-0 bg-white/20 blur-xl mix-blend-overlay"
                                                    />
                                                 </motion.h2>
                                                 
                                                 <motion.span 
                                                    initial={{ opacity: 0, y: 10 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 1 + (i * 0.1), duration: 0.6 }}
                                                    className="text-[10px] md:text-xl text-blue-400 font-light tracking-[0.2em] uppercase mt-2 border-b border-blue-500/20 pb-1"
                                                 >
                                                    {part.w}
                                                 </motion.span>
                                             </div>
                                         )}
                                     </React.Fragment>
                                 ))}

                                 {/* Global Sweeping Shimmer Overlaying the letters row */}
                                 <motion.div 
                                    animate={{ x: ["-100%", "200%"] }}
                                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                                    className="absolute inset-0 top-0 h-[8.5vw] bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-[-30deg] mix-blend-overlay z-20 pointer-events-none"
                                 />
                             </div>

                             <motion.p 
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.8, duration: 1 }}
                                className="mt-16 text-xl md:text-3xl text-gray-400 font-light max-w-3xl text-center px-6"
                             >
                                {item.description}
                             </motion.p>
                         </div>
                    ) : (
                        <h2 className={`${isTextOnly ? 'text-4xl md:text-[8vw] font-medium text-white mb-12 tracking-tighter leading-none uppercase' : 'text-5xl md:text-8xl font-medium text-white mb-8 tracking-tighter leading-tight'}`}>
                            {item.title}
                        </h2>
                    )}
                    
                    {item.title !== "S.H.A.D.E" && (
                        <p className={`${isTextOnly ? 'text-2xl md:text-4xl text-gray-400 font-light max-w-4xl mx-auto leading-tight' : 'text-xl md:text-3xl text-gray-400 font-light max-w-3xl mx-auto leading-relaxed'}`}>
                            {item.description}
                        </p>
                    )}
                </motion.div>
                
                {isTextOnly && (
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(29,78,216,0.15)_0%,_transparent_70%)] pointer-events-none" />
                )}
            </div>
        </div>
    );
};

// ----------------------------------------------------------------------------
// MAIN LAYOUT WRAPPER
// ----------------------------------------------------------------------------
const CinematicProductLayout = ({ children }) => {
  return (
    <div className="bg-black min-h-screen text-white font-secondary selection:bg-white selection:text-black">
      {children}
    </div>
  );
};

export default CinematicProductLayout;
// ----------------------------------------------------------------------------
// 12. FUTURISTIC BACKGROUND
// ----------------------------------------------------------------------------
export const FuturisticBackground = () => {
    return (
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
            {/* The Grid */}
            <div 
                className="absolute inset-0 opacity-[0.1]" 
                style={{ 
                    backgroundImage: `radial-gradient(circle at 1px 1px, #3b82f6 1px, transparent 0)`,
                    backgroundSize: '40px 40px' 
                }} 
            />
            
            {/* Peripheral Glows */}
            <div className="hidden md:block absolute top-0 left-0 w-[50vw] h-[50vh] bg-blue-600/5 blur-[120px] rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="hidden md:block absolute bottom-0 right-0 w-[50vw] h-[50vh] bg-indigo-600/5 blur-[120px] rounded-full translate-x-1/2 translate-y-1/2" />

            {/* Floating Data Points */}
            <div className="absolute inset-0">
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        animate={{ 
                            y: [0, -100, 0],
                            opacity: [0, 0.3, 0],
                            scale: [1, 1.2, 1]
                        }}
                        transition={{ 
                            duration: 10 + Math.random() * 20,
                            repeat: Infinity,
                            delay: Math.random() * 20
                        }}
                        className="absolute w-1 h-1 bg-blue-400 rounded-full"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>
        </div>
    );
};
